import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getStoredChatId } from './chatId';
import {
  useUserCards, useCardsOrder, sortByOrder, useCardStatement, formatMoney, cardIcon, cardQuery, MAIN_CARD_ID,
} from './cardsStore';
import CapitalPage, { readRates } from './CapitalPage';
import { CATEGORY_BY_KEY, normalizeCategory } from './categories';
import { useBalancesHidden, useMaskBalance, HIDDEN_BALANCE } from './balanceVisibility';
import './StatisticsPage.css';

const PAGE_ANIM_MS = 300; // синхронізовано з анімацією в StatisticsPage.css

// ---------- Іконки (білі лінійні, як у monobank) ----------

const svgProps = {
  viewBox: '0 0 24 24', fill: 'none', stroke: '#fff', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round',
};

const Icons = {
  ArrowUpRight: () => <svg {...svgProps} strokeWidth={2.2}><path d="M7 17L17 7M9 7h8v8" /></svg>,
  ArrowDownLeft: () => <svg {...svgProps} strokeWidth={2.2}><path d="M17 7L7 17M15 17H7V9" /></svg>,
  Box: () => (
    <svg viewBox="0 0 24 24" fill="#fff">
      <rect x="3" y="4" width="18" height="5" rx="1.5" />
      <path d="M4.5 10.5h15V18a2 2 0 0 1-2 2h-11a2 2 0 0 1-2-2z" />
      <rect x="9" y="12.5" width="6" height="1.8" rx=".9" fill="#2ba7e2" />
    </svg>
  ),
  Card: () => (
    <svg viewBox="0 0 24 24" fill="#fff">
      <rect x="3" y="5.5" width="18" height="13" rx="2.5" />
      <rect x="3" y="9" width="18" height="2.4" fill="#ff1f9b" />
      <rect x="5.5" y="14" width="5" height="1.8" rx=".9" fill="#ff1f9b" />
    </svg>
  ),
  JarCoins: () => <svg {...svgProps}><rect x="5" y="3.5" width="8" height="2.5" rx="1" /><path d="M5.5 6C4.3 7.2 4 8 4 9.5v7A2 2 0 0 0 6 18.5h4" /><path d="M12.5 6c.7.7 1.1 1.3 1.3 2" /><ellipse cx="16.5" cy="13" rx="3.5" ry="1.5" /><path d="M13 13v4.5c0 .8 1.6 1.5 3.5 1.5s3.5-.7 3.5-1.5V13M13 15.3c0 .8 1.6 1.5 3.5 1.5s3.5-.7 3.5-1.5" /></svg>,
  MonoCard: () => <svg {...svgProps}><rect x="3" y="6" width="18" height="12" rx="2" /><text x="12" y="14.3" textAnchor="middle" fontSize="6.5" fontWeight="700" fill="#fff" stroke="none" fontFamily="-apple-system, sans-serif">m</text></svg>,
  Download: () => <svg {...svgProps}><path d="M12 3v11M8 10.5l4 4 4-4" /><path d="M4 12v6.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V12" /></svg>,
  Cash: () => <svg {...svgProps}><rect x="3" y="6" width="18" height="11" rx="1.5" /><path d="M2.5 19.5h19" /><path d="M10 9.5c.6-.6 1.3-.8 2-.8 1.3 0 2.2.8 2.2 1.8 0 1.6-2.4 2-4.2 3.8h4.4M9.5 11.8h5M9.5 13.3h5" /></svg>,
  Grid: () => <svg viewBox="0 0 24 24" fill="#3c3e55"><rect x="6.5" y="6.5" width="4.5" height="4.5" rx="1.3" /><rect x="13" y="6.5" width="4.5" height="4.5" rx="1.3" /><rect x="6.5" y="13" width="4.5" height="4.5" rx="1.3" /><rect x="13" y="13" width="4.5" height="4.5" rx="1.3" /></svg>,
  Hash: () => <svg viewBox="0 0 24 24" fill="none" stroke="#3c3e55" strokeWidth="2.2" strokeLinecap="round"><path d="M10 6l-2 12M16 6l-2 12M6.5 10h12M5.5 14h12" /></svg>,
  Back: () => <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12H4M10 6l-6 6 6 6" /></svg>,
  Coins: () => (
    <svg viewBox="0 0 24 24" fill="#fff">
      <ellipse cx="9" cy="5" rx="6" ry="2.4" />
      <path d="M3 6.6c0 1.3 2.7 2.4 6 2.4s6-1.1 6-2.4V9c0 1.3-2.7 2.4-6 2.4S3 10.3 3 9z" />
      <path d="M3 10.6c0 1.3 2.7 2.4 6 2.4s6-1.1 6-2.4V13c0 1.3-2.7 2.4-6 2.4S3 14.3 3 13z" />
      <ellipse cx="15" cy="14" rx="6" ry="2.4" />
      <path d="M9 15.6c0 1.3 2.7 2.4 6 2.4s6-1.1 6-2.4V18c0 1.3-2.7 2.4-6 2.4S9 19.3 9 18z" />
      <path d="M9 19.6c0 1.3 2.7 2.4 6 2.4s6-1.1 6-2.4V21c0 .8-2.7 1.5-6 1.5S9 21.8 9 21z" />
    </svg>
  ),
  Calendar: () => <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.7" strokeLinecap="round"><rect x="4" y="5.5" width="16" height="14" rx="2" /><path d="M4 10h16M8.5 3.5v4M15.5 3.5v4M9 14.5h6" /></svg>,
  More: () => <svg viewBox="0 0 24 24" fill="#fff"><circle cx="12" cy="4.1" r="1.85" /><circle cx="12" cy="12" r="1.85" /><circle cx="12" cy="19.9" r="1.85" /></svg>,
};

// ---------- Розкладка операцій ----------

// Слайди каруселі в порядку monobank
const SLIDES = [
  { key: 'expense', title: 'Витрати', color: '#ff7a1a', emoji: '🤞', Icon: Icons.ArrowUpRight },
  { key: 'savings', title: 'На заощадження', color: '#2ba7e2', emoji: '🖖', Icon: Icons.Box },
  { key: 'own', title: 'На свої картки', color: '#ff1f9b', emoji: '🤝', Icon: Icons.Card },
  { key: 'income', title: 'Надходження', color: '#1ed34a', emoji: '😎', Icon: Icons.ArrowDownLeft },
];

const DARK_BG = '#2c2d36';
// Категорії витрат — вирізані іконки з categories.js; решта груп (свої картки,
// поповнення, зняття) своїх іконок не має — малюємо SVG на кольоровому колі
const CATEGORIES = {
  ...Object.fromEntries(Object.values(CATEGORY_BY_KEY).map((c) => [c.key, { label: c.uk, img: c.icon }])),
  own: { bg: 'linear-gradient(135deg, #868ca2, #d6d9e3)', Icon: Icons.MonoCard },
  topup: { label: 'Поповнення картки', bg: DARK_BG, Icon: Icons.Download },
  withdraw: { label: 'Часткове зняття', bg: 'linear-gradient(135deg, #d43f7f, #f8ab7e)', Icon: Icons.JarCoins },
  cash: { label: 'Поповнення готівкою', bg: DARK_BG, Icon: Icons.Cash },
};

// Окремої категорії для банок і своїх карток у БД нема — розпізнаємо за назвою,
// як її пише monobank у виписці («Поповнення «Мрія»», «На Білу картку»…)
function classify(tx) {
  const name = String(tx.name || '').trim();
  if (Number(tx.amount) < 0) {
    if (/^поповнення\s*(«|"|банки)/i.test(name)) return { slide: 'savings', cat: 'jar' };
    if (/^на\s+.+\s+картку$/i.test(name)) return { slide: 'own', cat: `own:${name.toLowerCase()}`, label: name };
    const cat = normalizeCategory(tx.category);
    return { slide: 'expense', cat: CATEGORY_BY_KEY[cat] ? cat : 'other' };
  }
  if (/^часткове зняття/i.test(name)) return { slide: 'income', cat: 'withdraw' };
  if (/^(city24|термінал)/i.test(name)) return { slide: 'income', cat: 'cash' };
  return { slide: 'income', cat: 'topup' };
}

// { expense: { total, count, groups: [{ key, label, total, count, meta }] }, … }
function buildStats(transactions, from, to) {
  const stats = Object.fromEntries(SLIDES.map((s) => [s.key, { total: 0, count: 0, groups: new Map() }]));
  transactions.forEach((tx) => {
    if (!tx || !tx.amount || !tx.date) return;
    const date = new Date(tx.date);
    if (date < from || date > to) return;
    const amount = Number(tx.amount);
    if (!amount) return;
    const { slide, cat, label } = classify(tx);
    const s = stats[slide];
    s.total += amount;
    s.count += 1;
    const g = s.groups.get(cat) || { key: cat, label: label || CATEGORIES[cat].label, meta: CATEGORIES[cat] || CATEGORIES.own, total: 0, count: 0, items: [] };
    g.total += amount;
    g.count += 1;
    g.items.push(tx);
    s.groups.set(cat, g);
  });
  Object.values(stats).forEach((s) => {
    s.groups = [...s.groups.values()].sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
  });
  return stats;
}

// ---------- Формати ----------

function plural(n, [one, few, many]) {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}
const TX_WORDS = ['транзакція', 'транзакції', 'транзакцій'];
const CAT_WORDS = ['Категорія', 'Категорії', 'Категорій'];

const MONTHS_GEN = ['січня', 'лютого', 'березня', 'квітня', 'травня', 'червня', 'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня'];
const MONTHS_NOM = ['Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень', 'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];

// «1 жовтня - 17 жовтня 2026» (рік у першій даті — лише якщо роки різні);
// один день — «16 жовтня 2026»
function formatRange(from, to) {
  if (from.toDateString() === to.toDateString()) return `${to.getDate()} ${MONTHS_GEN[to.getMonth()]} ${to.getFullYear()}`;
  const left = `${from.getDate()} ${MONTHS_GEN[from.getMonth()]}${from.getFullYear() !== to.getFullYear() ? ` ${from.getFullYear()}` : ''}`;
  return `${left} - ${to.getDate()} ${MONTHS_GEN[to.getMonth()]} ${to.getFullYear()}`;
}

// Період — календарний місяць; поточний — до сьогодні
function monthRange(year, month) {
  const now = new Date();
  const from = new Date(year, month, 1);
  const isCurrent = year === now.getFullYear() && month === now.getMonth();
  const to = isCurrent ? now : new Date(year, month + 1, 0);
  return { from, to: new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59, 999), year, month };
}

const MONTH_OPTIONS = 12; // скільки місяців назад можна обрати

const percentOf = (part, total) => (total ? Math.max(1, Math.round((Math.abs(part) / Math.abs(total)) * 100)) : 0);

// ---------- Компоненти ----------

function CategoryIcon({ meta, className }) {
  if (meta.img) return <img className={className} src={meta.img} alt="" />;
  return <div className={className} style={{ background: meta.bg }}><meta.Icon /></div>;
}

function CategoryList({ groups, total, currency, income, onSelect }) {
  const maskBalance = useMaskBalance();
  if (!groups.length) return <p className="stats-empty">За цей період операцій немає</p>;
  return (
    <div className="stats-list">
      {groups.map((g) => (
        <div className="stats-row" key={g.key} onClick={() => onSelect(g.key)}>
          <CategoryIcon meta={g.meta} className="stats-row-icon" />
          <div className="stats-row-text">
            <p className="stats-row-name">{g.label}</p>
            <p className="stats-row-sub">{g.count} {plural(g.count, TX_WORDS)}</p>
          </div>
          <div className="stats-row-right">
            <p className={`stats-row-sum ${income ? 'income' : ''}`}>{maskBalance(formatMoney(g.total, currency))}</p>
            <p className="stats-row-sub">{percentOf(g.total, total)}%</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function Segmented({ value, onChange }) {
  return (
    <div className="stats-segmented">
      <span className={`stats-segmented-pill ${value === 'tags' ? 'right' : ''}`} />
      <button type="button" className={value === 'categories' ? 'active' : ''} onClick={() => onChange('categories')}>Категорії</button>
      <button type="button" className={value === 'tags' ? 'active' : ''} onClick={() => onChange('tags')}>Теги</button>
    </div>
  );
}

function Breakdown({ tab, stat, currency, income, onSelect }) {
  if (tab === 'tags') return <p className="stats-empty">Тегів поки немає</p>;
  return <CategoryList groups={stat.groups} total={stat.total} currency={currency} income={income} onSelect={onSelect} />;
}

const MONEY_ANIM_MS = 700; // як анімація балансу на головній

// Сума, що «докручується» до нового значення (easeOutCubic, як баланс на
// головній). Останнє показане значення зберігаємо в cache[id], тож при
// перемонтуванні (повернення з деталей) анімація не стартує знову з нуля.
function AnimatedMoney({ id, value, currency, cache, className }) {
  const maskBalance = useMaskBalance();
  const [shown, setShown] = useState(() => cache.current[id] ?? 0);
  useEffect(() => {
    const from = cache.current[id] ?? 0;
    // Змінили валюту — нова сума одразу, без докручування
    const currencyChanged = cache.current[`${id}:currency`] !== undefined && cache.current[`${id}:currency`] !== currency;
    cache.current[`${id}:currency`] = currency;
    if (from === value || currencyChanged) { cache.current[id] = value; setShown(value); return undefined; }
    const start = performance.now();
    let raf;
    function tick(now) {
      const t = Math.min((now - start) / MONEY_ANIM_MS, 1);
      const current = from + (value - from) * (1 - Math.pow(1 - t, 3));
      cache.current[id] = current;
      setShown(current);
      if (t < 1) raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [id, value, currency, cache]);
  // Поки ефект ще не спрацював після зміни валюти — показуємо нову суму, а не стару
  const instant = cache.current[`${id}:currency`] !== undefined && cache.current[`${id}:currency`] !== currency;
  return <p className={className}>{maskBalance(formatMoney(instant ? value : shown, currency))}</p>;
}

// «-900.62 ₴» → ціла частина більшим шрифтом, «.62 ₴» — меншим (як у monobank)
function SplitAmount({ value, currency, className }) {
  const [hidden] = useBalancesHidden();
  if (hidden) return <p className={className}>{HIDDEN_BALANCE}</p>;
  const [int, rest] = formatMoney(value, currency).split('.');
  return (
    <p className={className}>
      {int}<span className="cat-tx-sum-dec">.{rest}</span>
    </p>
  );
}

// Іконки категорій бота (icons-bot/categories) — коло на квадратному фоні,
// тож збільшуємо картинку, щоб коло заповнило всю аватарку
function TxImage({ src }) {
  if (src.includes('/icons-bot/categories/')) {
    return <span className="cat-tx-img cat-tx-img--zoom"><img src={src} alt="" /></span>;
  }
  return <img className="cat-tx-img" src={src} alt="" />;
}

// Операції однієї категорії за обраний період, згруповані по місяцях
function CategoryTransactions({ group, rangeText, currency, income, closing, onBack, onOpen }) {
  const now = new Date();
  const months = [];
  [...group.items]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .forEach((tx) => {
      const d = new Date(tx.date);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      let m = months[months.length - 1];
      if (!m || m.key !== key) {
        m = { key, label: `${MONTHS_NOM[d.getMonth()]}${d.getFullYear() !== now.getFullYear() ? ` ${d.getFullYear()}` : ''}`, items: [] };
        months.push(m);
      }
      m.items.push(tx);
    });

  return (
    <div className={`cat-view ${closing ? 'closing' : ''}`}>
      <div className="cat-header">
        <button type="button" className="capital-back" onClick={onBack} aria-label="Назад"><Icons.Back /></button>
        <h1 className="cat-title">{group.label}</h1>
        <p className="cat-range">{rangeText}</p>
      </div>
      {months.map((m) => (
        <div className="cat-month" key={m.key}>
          <p className="cat-month-label">{m.label}</p>
          {m.items.map((tx, i) => (
            <div className="cat-tx" key={`${tx.id}-${i}`} onClick={() => onOpen(tx)}>
              {tx.img
                ? <TxImage src={tx.img} />
                : <CategoryIcon meta={group.meta} className="cat-tx-img" />}
              <p className="cat-tx-name">{tx.name}</p>
              <SplitAmount className={`cat-tx-sum ${income ? 'income' : ''}`} value={tx.amount} currency={currency} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

const CARD_WORDS = ['картка', 'картки', 'карток'];

// «Чорна картка» або «Чорна, Біла і Доларова картки» (якщо всі назви на «картка»)
function cardsTitle(cards) {
  if (cards.length === 1) return cards[0].name;
  const sameSuffix = cards.every((c) => /\sкартка$/i.test(c.name));
  const names = cards.map((c) => (sameSuffix ? c.name.replace(/\sкартка$/i, '') : c.name));
  const joined = `${names.slice(0, -1).join(', ')} і ${names[names.length - 1]}`;
  return sameSuffix ? `${joined} ${plural(cards.length, CARD_WORDS)}` : joined;
}

const cardThumb = (card) => cardIcon(card.type) || (card.id === MAIN_CARD_ID ? '/card-icons/black.png' : null);

const STACK_STEP = 6; // на скільки визирає кожна наступна картка в стопці

function CardThumb({ card, className, style }) {
  const src = cardThumb(card);
  return src
    ? <img className={className} src={src} alt="" style={style} />
    : <span className={`${className} stats-card-img--placeholder`} style={style} />;
}

// «Джерело» — вибір однієї чи кількох карток, застосовується кнопкою «Показати»
function SourceSheet({ cards, selected, onApply, onClose }) {
  const [draft, setDraft] = useState(selected);
  const maskBalance = useMaskBalance();
  const toggle = (id) => setDraft((d) => (d.includes(id) ? d.filter((x) => x !== id) : [...d, id]));
  return (
    <div className="stats-sheet-root" onClick={onClose}>
      <div className="source-sheet" onClick={(e) => e.stopPropagation()}>
        <p className="source-sheet-title">Джерело</p>
        {cards.map((c) => {
          const on = draft.includes(c.id);
          return (
            <button type="button" key={c.id} className="source-row" onClick={() => toggle(c.id)}>
              <CardThumb card={c} className="source-row-img" />
              <span className="source-row-text">
                <span className="source-row-name">{c.name}</span>
                <span className="source-row-balance">{maskBalance(formatMoney(c.balance, c.currency))}</span>
              </span>
              <span className={`source-check ${on ? 'on' : ''}`}>
                {on && <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M6.5 12.5l3.5 3.5 7.5-8" /></svg>}
              </span>
            </button>
          );
        })}
        <button type="button" className="source-apply" disabled={!draft.length} onClick={() => onApply(draft)}>Показати</button>
      </div>
    </div>
  );
}

// «Валюта» — у чому показувати статистику, застосовується кнопкою «Показати»
const DISPLAY_CURRENCIES = [
  { code: 'UAH', name: 'Гривня', symbol: '₴' },
  { code: 'USD', name: 'Долар', symbol: '$' },
  { code: 'EUR', name: 'Євро', symbol: '€' },
];

function CurrencySheet({ selected, onApply, onClose }) {
  const [draft, setDraft] = useState(selected);
  return (
    <div className="stats-sheet-root" onClick={onClose}>
      <div className="source-sheet" onClick={(e) => e.stopPropagation()}>
        <p className="source-sheet-title">Валюта</p>
        {DISPLAY_CURRENCIES.map((c) => (
          <button type="button" key={c.code} className="source-row" onClick={() => setDraft(c.code)}>
            <span className={`currency-row-icon currency-row-icon--${c.code.toLowerCase()}`}>
              <span className="currency-row-symbol">{c.symbol}</span>
            </span>
            <span className="source-row-text">
              <span className="source-row-name">{c.name}</span>
            </span>
            <span className={`currency-radio ${draft === c.code ? 'on' : ''}`} />
          </button>
        ))}
        <button type="button" className="source-apply" onClick={() => onApply(draft)}>Показати</button>
      </div>
    </div>
  );
}

// ---------- Вибір періоду: календар, як у monobank ----------

const WEEKDAYS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'нд'];
const dayStart = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const dayEnd = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
const sameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const shortDate = (d) => `${d.getDate()} ${MONTHS_GEN[d.getMonth()]}`;

// Місяці від найстаршого до поточного; дні після сьогодні недоступні.
// Перший тап — початок періоду, другий — кінець (раніше за початок — міняємо
// місцями); можна обрати й один день — тоді статистика за нього.
function PeriodSheet({ range, onApply, onClose }) {
  const today = dayStart(new Date());
  const [from, setFrom] = useState(dayStart(range.from));
  const [to, setTo] = useState(dayStart(range.to));
  const listRef = useRef(null);

  // + наступний місяць (весь неактивний), як у monobank
  const months = useMemo(() => Array.from({ length: MONTH_OPTIONS + 1 }, (_, i) => {
    const first = new Date(today.getFullYear(), today.getMonth() - (MONTH_OPTIONS - 1 - i), 1);
    const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
    const offset = (first.getDay() + 6) % 7; // понеділок — перший
    return { first, offset, days: Array.from({ length: daysInMonth }, (_, d) => new Date(first.getFullYear(), first.getMonth(), d + 1)) };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  // Відкриваємо на місяці, де починається обраний період
  useEffect(() => {
    const el = listRef.current?.querySelector('[data-start="1"]');
    // місяць початку — на ~221px нижче днів тижня, як у monobank (видно кінець попереднього)
    if (el && listRef.current) listRef.current.scrollTop = el.offsetTop - listRef.current.offsetTop - 221;
  }, []);

  function pick(d) {
    if (d > today) return;
    if (!from || to) { setFrom(d); setTo(null); return; }
    if (d < from) { setTo(from); setFrom(d); return; }
    setTo(d);
  }

  const end = to || from;
  return (
    <div className="stats-sheet-root" onClick={onClose}>
      <div className="period-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="period-head">
          <p className="period-title">Період</p>
          <p className="period-sub">{from ? `${shortDate(from)}${end && !sameDay(from, end) ? ` - ${shortDate(end)}` : ''}` : 'Оберіть дати'}</p>
        </div>
        <div className="period-weekdays">{WEEKDAYS.map((w) => <span key={w}>{w}</span>)}</div>
        <div className="period-list" ref={listRef}>
          {months.map((m) => (
            <div className="period-month" key={m.first.getTime()} data-start={from && m.first.getFullYear() === from.getFullYear() && m.first.getMonth() === from.getMonth() ? '1' : undefined}>
              <p className="period-month-label">{MONTHS_NOM[m.first.getMonth()]}{m.first.getFullYear() !== today.getFullYear() ? ` ${m.first.getFullYear()}` : ''}</p>
              <div className="period-grid">
                {m.offset > 0 && <span style={{ gridColumn: `span ${m.offset}` }} />}
                {m.days.map((d) => {
                  const future = d > today;
                  const inRange = from && end && d >= from && d <= end;
                  const cls = [
                    'period-day',
                    future && 'future',
                    inRange && 'selected',
                    inRange && sameDay(d, from) && 'start',
                    inRange && sameDay(d, end) && 'end',
                  ].filter(Boolean).join(' ');
                  return (
                    <button type="button" key={d.getDate()} className={cls} disabled={future} onClick={() => pick(d)}>
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <button type="button" className="period-apply" disabled={!from} onClick={() => onApply({ from: dayStart(from), to: dayEnd(end) })}>
          Показати
        </button>
      </div>
    </div>
  );
}

// ---------- Сторінка ----------

// Статистика витрат/надходжень обраних карток за місяць (з кнопки
// статистики в шапці головної). Стартова картка — location.state.cardId,
// у «Джерело» можна обрати кілька.
export default function StatisticsPage() {
  const navigate = useNavigate();
  const maskBalance = useMaskBalance();
  const location = useLocation();
  const chatId = getStoredChatId();

  const [selectedIds, setSelectedIds] = useState(() => [location.state?.cardId || MAIN_CARD_ID]);
  // Основна картка — balance/ і transactions/; додаткові приходять разом із платежами
  const mainStatement = useCardStatement(chatId, MAIN_CARD_ID);
  const openedCards = useUserCards(chatId);
  const cardsOrder = useCardsOrder(chatId);

  const cards = useMemo(() => {
    const main = {
      id: MAIN_CARD_ID, type: MAIN_CARD_ID, name: 'Чорна картка', currency: 'UAH',
      balance: mainStatement.balance, transactions: mainStatement.transactions,
    };
    return sortByOrder([main, ...openedCards], cardsOrder);
  }, [openedCards, cardsOrder, mainStatement.balance, mainStatement.transactions]);

  // Закриті картки прибираємо з вибору; нічого не лишилось — основна
  const selectedCards = cards.filter((c) => selectedIds.includes(c.id));
  const shownCards = selectedCards.length ? selectedCards : [cards.find((c) => c.id === MAIN_CARD_ID)];

  // Валюта з попапу «Валюта»; поки не обрали — одна валюта карток показується
  // в ній, різні зводимо в гривню. Чуже перераховуємо через гривню за курсом купівлі
  const [pickedCurrency, setPickedCurrency] = useState(null);
  const currencies = [...new Set(shownCards.map((c) => c.currency || 'UAH'))];
  const currency = pickedCurrency || (currencies.length === 1 ? currencies[0] : 'UAH');
  const rates = currencies.some((c) => c !== currency) ? readRates() : null;
  const toShown = (amount, cardCurrency) => {
    const value = Number(amount || 0);
    const from = cardCurrency || 'UAH';
    if (!rates || from === currency) return value;
    return rates[currency] ? (value * (rates[from] ?? 0)) / rates[currency] : 0;
  };

  const balance = shownCards.reduce((sum, c) => sum + toShown(c.balance, c.currency), 0);
  const transactions = useMemo(
    () => shownCards.flatMap((c) => (c.transactions || []).map((tx) => (
      // _cardId — щоб відкрити деталі з виписки потрібної картки
      { ...tx, _cardId: c.id, ...(rates ? { amount: String(toShown(tx.amount, c.currency)) } : {}) }
    ))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cards, selectedIds.join(), currency]
  );

  const now = new Date();
  const [range, setRange] = useState(() => monthRange(now.getFullYear(), now.getMonth()));
  const stats = useMemo(
    () => buildStats(transactions, range.from, range.to),
    [transactions, range]
  );

  // За період нема жодної операції — замість каруселі показуємо кота
  const isEmpty = SLIDES.every((s) => !stats[s.key].count);

  const [slideIndex, setSlideIndex] = useState(0);
  const [tab, setTab] = useState('categories');
  const [detail, setDetail] = useState(null); // ключ слайда, відкритого на весь екран
  const [category, setCategory] = useState(null); // { slide, key } — операції однієї категорії
  const [sheet, setSheet] = useState(null); // 'card' | 'period' | 'currency'
  const [capitalOpen, setCapitalOpen] = useState(false);
  const carouselRef = useRef(null);
  const pageRef = useRef(null);
  const sumsCache = useRef({}); // останні показані суми слайдів (AnimatedMoney)

  const catsRef = useRef(null);

  // Два положення сторінки: верх і категорії (під шапкою). Між ними нативного
  // скролу нема: щойно палець зрушив униз/угору — самі доїжджаємо до потрібного
  // положення, без інерції. Нижче категорій список скролиться як звичайно.
  const snapState = useRef({});
  snapState.current = { active: !detail && !category && !sheet && !capitalOpen && !isEmpty };
  const syncTouchAction = useRef(() => {});
  useEffect(() => { syncTouchAction.current(); });
  useEffect(() => {
    const el = pageRef.current;
    if (!el) return undefined;
    const TRIGGER_DY = 8;
    const SNAP_MS = 450;
    let start = null;
    let raf = null;
    let animating = false;
    let lastTop = 0;

    const catsTop = () => {
      const cats = catsRef.current;
      const bar = el.querySelector('.stats-topbar');
      if (!cats) return null;
      const t = cats.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop - (bar ? bar.offsetHeight : 0) - 12;
      return Math.min(Math.round(t), el.scrollHeight - el.clientHeight);
    };

    // У верхньому положенні вертикальний нативний скрол вимкнено через
    // touch-action (карусель лишається горизонтальною) — інакше iOS починає
    // скролити сам і наш touchmove уже не скасувати
    const sync = () => {
      const target = catsTop();
      const top = snapState.current.active && target != null && el.scrollTop < target - 1;
      el.style.touchAction = top ? 'pan-x' : '';
    };
    syncTouchAction.current = sync;
    sync();

    // Покадровий scrollTop на iOS смикається, тож їдемо transform'ом тіла
    // сторінки (композитинг на GPU, як у каруселі), а в кінці підміняємо його
    // справжнім scrollTop. overflow: hidden на цей час гасить інерцію iOS.
    let endTimer = null;
    const snapTo = (to) => {
      const from = el.scrollTop;
      const body = el.querySelector('.stats-body');
      if (Math.abs(from - to) < 1 || !body) return;
      animating = true;
      el.style.overflowY = 'hidden';
      body.style.willChange = 'transform';
      body.style.transition = 'none';
      body.style.transform = 'translate3d(0, 0, 0)';
      raf = requestAnimationFrame(() => {
        raf = requestAnimationFrame(() => {
          body.style.transition = `transform ${SNAP_MS}ms cubic-bezier(0.25, 1, 0.5, 1)`;
          body.style.transform = `translate3d(0, ${from - to}px, 0)`;
        });
      });
      endTimer = setTimeout(() => {
        body.style.transition = 'none';
        body.style.transform = '';
        body.style.willChange = '';
        el.scrollTop = to;
        el.style.overflowY = '';
        lastTop = el.scrollTop;
        animating = false;
        sync();
      }, SNAP_MS + 20);
    };

    const onStart = (e) => {
      if (!snapState.current.active || animating || e.touches.length !== 1) { start = null; return; }
      start = { x: e.touches[0].clientX, y: e.touches[0].clientY, top: el.scrollTop, axis: null };
    };
    const onMove = (e) => {
      if (!start) return;
      if (animating) { if (e.cancelable) e.preventDefault(); return; }
      const dx = e.touches[0].clientX - start.x;
      const dy = e.touches[0].clientY - start.y;
      if (!start.axis && Math.hypot(dx, dy) > 4) start.axis = Math.abs(dy) > Math.abs(dx) ? 'y' : 'x';
      if (start.axis !== 'y') return;
      const target = catsTop();
      if (target == null || target <= 0) return;
      const atTop = start.top < target - 1;
      const atCats = Math.abs(start.top - target) <= 1;
      if (atTop) {
        if (e.cancelable) e.preventDefault();
        if (dy < -TRIGGER_DY) { start = null; snapTo(target); }
      } else if (atCats && dy > 0) {
        if (e.cancelable) e.preventDefault();
        if (dy > TRIGGER_DY) { start = null; snapTo(0); }
      }
    };
    const onEnd = () => { start = null; };
    // Інерція списку вгору заїхала вище категорій — одразу наверх
    const onScroll = () => {
      const top = el.scrollTop;
      const up = top < lastTop;
      lastTop = top;
      if (!animating) sync();
      if (animating || start || !snapState.current.active || !up) return;
      const target = catsTop();
      if (target != null && top > 0 && top < target - 1) snapTo(0);
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(endTimer);
      el.style.overflowY = '';
      el.style.touchAction = '';
      syncTouchAction.current = () => {};
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
      el.removeEventListener('scroll', onScroll);
    };
  }, []);

  // Перемикання головна ↔ деталі — завжди з початку сторінки
  useEffect(() => {
    if (pageRef.current) pageRef.current.scrollTop = 0;
  }, [detail]);

  function handleCarouselScroll(e) {
    const el = e.currentTarget;
    const slide = el.firstElementChild;
    if (!slide) return;
    const step = slide.offsetWidth + parseFloat(getComputedStyle(el).columnGap || 0);
    const idx = Math.round(el.scrollLeft / step);
    if (idx !== slideIndex) setSlideIndex(Math.max(0, Math.min(SLIDES.length - 1, idx)));
  }

  function scrollToSlide(idx) {
    const el = carouselRef.current;
    const slide = el?.children[idx];
    if (el && slide) el.scrollTo({ left: slide.offsetLeft - el.offsetLeft - (el.offsetWidth - slide.offsetWidth) / 2, behavior: 'smooth' });
  }

  // Закриття зі слайдом вправо, як у деталей транзакції
  const [closing, setClosing] = useState(false);
  const closeTimer = useRef(null);
  // Операції категорії — окремий екран, що виїжджає справа і так само закривається
  const [catClosing, setCatClosing] = useState(false);
  const catCloseTimer = useRef(null);
  useEffect(() => () => clearTimeout(catCloseTimer.current), []);
  function handleBack() {
    if (category) {
      if (catClosing) return;
      setCatClosing(true);
      catCloseTimer.current = setTimeout(() => { setCategory(null); setCatClosing(false); }, PAGE_ANIM_MS);
      return;
    }
    if (detail) { setDetail(null); return; }
    if (closing) return;
    setClosing(true);
    closeTimer.current = setTimeout(() => navigate(-1), PAGE_ANIM_MS);
  }
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  // Під home-indicator iOS малює фон <body> — фарбуємо його в колір сторінки
  useEffect(() => {
    const original = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#1b1c2d';
    return () => { document.body.style.backgroundColor = original; };
  }, []);

  const current = SLIDES[slideIndex];
  const detailSlide = SLIDES.find((s) => s.key === detail);
  const rangeText = formatRange(range.from, range.to);

  // Категорія могла зникнути після зміни періоду/карток — тоді просто не показуємо
  const categoryGroup = category && stats[category.slide].groups.find((g) => g.key === category.key);

  return (
    <div className={`stats-page ${closing ? 'closing' : ''}`} ref={pageRef}>
      <>
      <div className="stats-topbar">
        <button type="button" className="stats-icon-btn" onClick={handleBack} aria-label="Назад"><Icons.Back /></button>
        {detailSlide && (
          <div className="stats-topbar-title">
            <p className="stats-topbar-main">{detailSlide.title} {maskBalance(formatMoney(stats[detail].total, currency))}</p>
            <p className="stats-topbar-sub">{rangeText}</p>
          </div>
        )}
        <button type="button" className="stats-icon-btn stats-coins" onClick={() => setCapitalOpen(true)} aria-label="Капітал"><Icons.Coins /></button>
      </div>

      {detailSlide ? (
        <div className="stats-body" key="detail">
          <Segmented value={tab} onChange={setTab} />
          <Breakdown tab={tab} stat={stats[detail]} currency={currency} income={detail === 'income'} onSelect={(key) => setCategory({ slide: detail, key })} />
        </div>
      ) : (
        <div className="stats-body" key="main">
          <h1 className="stats-title">Статистика</h1>
          <div className="stats-range">
            <span>{rangeText}</span>
            <button type="button" className="stats-range-btn" onClick={() => setSheet('period')} aria-label="Обрати період"><Icons.Calendar /></button>
          </div>

          <button type="button" className="stats-card" onClick={() => setSheet('card')}>
            {/* Кілька карток — стопкою: верхня повністю, з-під неї визирають краї наступних */}
            <span className="stats-card-stack" style={{ height: 30 + STACK_STEP * (Math.min(shownCards.length, 3) - 1) }}>
              {shownCards.slice(0, 3).map((c, i) => (
                <CardThumb key={c.id} card={c} className="stats-card-img" style={{ top: STACK_STEP * i, zIndex: 3 - i }} />
              ))}
            </span>
            <span className="stats-card-text">
              <span className="stats-card-name">{cardsTitle(shownCards)}</span>
              {shownCards.length === 1 && <span className="stats-card-balance">{maskBalance(formatMoney(balance, currency))}</span>}
            </span>
            <span className="stats-card-more"><Icons.More /></span>
          </button>

          {isEmpty ? (
            <div className="stats-no-ops">
              <img className="stats-no-ops-img" src="/stats-empty-cat.png" alt="" />
              <p className="stats-no-ops-title">Немає операцій</p>
              <p className="stats-no-ops-text">За вибраний період за цим джерелом не було операцій</p>
            </div>
          ) : (<>
          <div className="stats-carousel" ref={carouselRef} onScroll={handleCarouselScroll}>
            {SLIDES.map((s) => {
              const st = stats[s.key];
              return (
                <div className="stats-slide" key={s.key}>
                  <div className="stats-slide-top">
                    <span className="stats-slide-icon" style={{ background: s.color }}><s.Icon /></span>
                    <button type="button" className="stats-currency" onClick={() => setSheet('currency')}>{currency}</button>
                  </div>
                  <p className="stats-slide-title">{s.title}</p>
                  <AnimatedMoney className="stats-slide-sum" id={s.key} value={st.total} currency={currency} cache={sumsCache} />
                  <div className="stats-slide-info">
                    <span className="stats-slide-emoji">{s.emoji}</span>
                    Усього ви зробили {st.count} {plural(st.count, TX_WORDS)}
                  </div>
                  <div className="stats-slide-tiles">
                    <div className="stats-tile">
                      <span className="stats-tile-icon"><Icons.Grid /></span>
                      <p className="stats-tile-num">{st.groups.length}</p>
                      <p className="stats-tile-label">{plural(st.groups.length, CAT_WORDS)}</p>
                    </div>
                    <div className="stats-tile">
                      <span className="stats-tile-icon"><Icons.Hash /></span>
                      <p className="stats-tile-num">0</p>
                      <p className="stats-tile-label">Тегів</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="stats-dots">
            {SLIDES.map((s, i) => (
              <button type="button" key={s.key} className={`stats-dot ${i === slideIndex ? 'active' : ''}`} onClick={() => scrollToSlide(i)} aria-label={s.title} />
            ))}
          </div>

          <div className="stats-cats" ref={catsRef}>
            <Segmented value={tab} onChange={setTab} />
            <Breakdown tab={tab} stat={stats[current.key]} currency={currency} income={current.key === 'income'} onSelect={(key) => setCategory({ slide: current.key, key })} />
          </div>
          </>)}
        </div>
      )}
      </>

      {categoryGroup && (
        <CategoryTransactions
          group={categoryGroup}
          rangeText={rangeText}
          currency={currency}
          income={category.slide === 'income'}
          closing={catClosing}
          onBack={handleBack}
          onOpen={(tx) => navigate(`/transaction/${tx.id}${cardQuery(tx._cardId)}`, { state: { from: 'statistics' } })}
        />
      )}

      {capitalOpen && (
        <CapitalPage
          cards={cards}
          onClose={() => setCapitalOpen(false)}
        />
      )}

      {sheet === 'card' && (
        <SourceSheet
          cards={cards}
          selected={shownCards.map((c) => c.id)}
          onApply={(ids) => { setSelectedIds(ids); setSheet(null); }}
          onClose={() => setSheet(null)}
        />
      )}

      {sheet === 'currency' && (
        <CurrencySheet
          selected={currency}
          onApply={(code) => { setPickedCurrency(code); setSheet(null); }}
          onClose={() => setSheet(null)}
        />
      )}

      {sheet === 'period' && (
        <PeriodSheet
          range={range}
          onApply={(r) => { setRange(r); setSheet(null); }}
          onClose={() => setSheet(null)}
        />
      )}
    </div>
  );
}
